import { useCallback, useMemo } from "react";
import { EagerLoaded } from "@common/types/base/orm";
import { Entity } from "@common/types/entity";
import { Invoice, InvoiceStatus } from "@common/types/invoice";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import {
    findIncomeAccountLedgerId,
    findPrimaryLedgerId,
} from "@lib/accountOverrides";
import { useScope } from "@state/scopeContext.lib";
import { useOverlays } from "contexts/OverlayContext";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useUser } from "@hooks/useUser";
import { useLedgers } from "@api/ledger.api";
import { useLastInvoiceNumber, useUpdateInvoice } from "@api/invoice.api";
import {
    useCreateNativeInvoice,
    usePreviewNativeInvoice,
} from "@api/native-invoice.api";
import { buildNativeInvoiceLineCodingByLineId } from "./buildNativeInvoiceLineCodingByLineId";
import { computeSuggestedInvoiceNumber } from "./computeSuggestedInvoiceNumber";
import { createNativeInvoiceFormState } from "./createNativeInvoiceFormState";
import { resolveFormState } from "./resolveNativeInvoiceFormState";
import { buildNativeInvoiceDraftBody } from "./buildNativeInvoiceDraftBody";

interface UseReviseNativeInvoiceParams {
    invoice: EagerLoaded<Invoice, "lines">;
    entity: Entity | undefined;
    // Gate the supporting queries (preview / ledgers / suggested number) so
    // they only run while the revise modal is open.
    enabled: boolean;
    // Called after the revision Draft is created and the original is voided.
    onRevised: () => void;
}

interface ReviseNativeInvoiceResult {
    isProcessing: boolean;
    isLoading: boolean;
    revise: (voidDate: string) => Promise<void>;
}

// Deterministic, retry-safe revision of an Open Kick-native invoice. The modal
// only supplies the void date; this hook orchestrates:
//   1. resolve the next invoice-number suggestion (same helper as create);
//   2. create (or idempotently recover) a Draft cloning the original's
//      snapshot, counterparty, line coding and design, with the next number
//      and a revisedFromInvoiceId link;
//   3. void the original with { status: VOID, voidedAt, revisionInvoiceId };
//   4. open the Draft in the full drawer.
// FE orchestration is intentionally non-atomic: idempotent create + lineage
// validation make retries deterministic without a dedicated revise endpoint.
export function useReviseNativeInvoice({
    invoice,
    entity,
    enabled,
    onRevised,
}: UseReviseNativeInvoiceParams): ReviseNativeInvoiceResult {
    const { workspaceId } = useScope();
    const currentUser = useUser();
    const { openOverlay } = useOverlays();
    const { error: toastError } = useToaster();

    const createMutation = useCreateNativeInvoice();
    const updateMutation = useUpdateInvoice();

    const previewQuery = usePreviewNativeInvoice({
        workspaceId: workspaceId ?? undefined,
        entityId: invoice.entityId,
        invoiceId: invoice.id,
        enabled,
    });
    const lastInvoiceNumberQuery = useLastInvoiceNumber({
        enabled,
        entityId: invoice.entityId,
    });
    const { data: ledgers } = useLedgers(
        enabled ? invoice.entityId : undefined,
    );

    const nextInvoiceNumber = useMemo(
        () =>
            computeSuggestedInvoiceNumber({
                isEdit: false,
                isError: lastInvoiceNumberQuery.isError,
                lastInvoiceNumber: lastInvoiceNumberQuery.data,
                entityName: entity?.name,
            }),
        [
            lastInvoiceNumberQuery.isError,
            lastInvoiceNumberQuery.data,
            entity?.name,
        ],
    );

    const isLoading =
        enabled &&
        (!previewQuery.isSuccess ||
            previewQuery.data?.renderSnapshot == null ||
            nextInvoiceNumber === undefined ||
            ledgers === undefined);

    const revise = useCallback(
        async (voidDate: string): Promise<void> => {
            const renderSnapshot = previewQuery.data?.renderSnapshot;
            if (
                !workspaceId ||
                !entity ||
                !renderSnapshot ||
                nextInvoiceNumber === undefined
            ) {
                toastError("Couldn't load the invoice to revise. Try again.");
                return;
            }

            const lineCodingByLineId = buildNativeInvoiceLineCodingByLineId(
                invoice.lines,
            );

            const { raw } = createNativeInvoiceFormState({
                mode: "edit",
                snapshot: renderSnapshot,
                customer: invoice.counterparty
                    ? {
                          id: invoice.counterparty.id,
                          name: invoice.counterparty.name,
                          email: invoice.counterparty.email,
                          cc: invoice.counterparty.cc,
                          bcc: invoice.counterparty.bcc,
                          phoneNumber: invoice.counterparty.phoneNumber,
                          street: invoice.counterparty.street,
                          city: invoice.counterparty.city,
                          state: invoice.counterparty.state,
                          postalCode: invoice.counterparty.postalCode,
                          country: invoice.counterparty.country,
                      }
                    : null,
                logoPreviewUrl: previewQuery.data?.logoUrl,
                lineCodingByLineId,
            });

            const resolved = resolveFormState(raw, {
                defaultEntity: entity,
                suggestedInvoiceNumber: nextInvoiceNumber,
                currentUserName: currentUser?.name,
                currentUserEmail: currentUser?.email,
            });
            const body = buildNativeInvoiceDraftBody({
                state: resolved,
                defaultEntity: entity,
                primaryAccrualLedgerId: findPrimaryLedgerId(
                    ledgers,
                    AccountingLedgerBasis.accruals,
                ),
                incomeAccountLedgerId: findIncomeAccountLedgerId(ledgers),
                // Revision creates a new Draft — emit full lineAccounting, not
                // a diff against the cloned coding (edit-mode baseline would
                // zero the diff and drop persisted coding).
                lineCodingBaselineByLineId: {},
            });
            if (!body) {
                toastError("Couldn't prepare the revision. Try again.");
                return;
            }

            let draftId: string;
            try {
                // Idempotent server-side: retrying after an uncertain response
                // returns the existing linked Draft rather than creating another.
                const created = await createMutation.mutateAsync({
                    workspaceId,
                    entityId: invoice.entityId,
                    body: { ...body, revisedFromInvoiceId: invoice.id },
                });
                draftId = created.data.id;
            } catch (error) {
                // Original stays Open; keep the modal open so the user can retry.
                const detail =
                    error instanceof Error ? error.message : undefined;
                toastError(
                    detail
                        ? `Couldn't create the revision: ${detail}`
                        : "Couldn't create the revision. Try again.",
                );
                return;
            }

            try {
                await updateMutation.mutateAsync({
                    workspaceId,
                    invoiceId: invoice.id,
                    status: InvoiceStatus.VOID,
                    voidedAt: voidDate,
                    revisionInvoiceId: draftId,
                });
            } catch (error) {
                // The linked Draft is saved; retry reuses it (idempotent create).
                const detail =
                    error instanceof Error ? error.message : undefined;
                toastError(
                    detail
                        ? `Revision draft created, but couldn't void the original invoice: ${detail}`
                        : "Revision draft created, but couldn't void the original invoice. Try again.",
                );
                return;
            }

            onRevised();
            openOverlay("nativeInvoiceDrawer", {
                invoiceId: draftId,
            });
        },
        [
            workspaceId,
            entity,
            invoice,
            previewQuery.data,
            nextInvoiceNumber,
            ledgers,
            currentUser?.name,
            currentUser?.email,
            createMutation,
            updateMutation,
            openOverlay,
            toastError,
            onRevised,
        ],
    );

    return {
        isProcessing: createMutation.isPending || updateMutation.isPending,
        isLoading,
        revise,
    };
}
